import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Plus,
  Trash2,
  Upload,
  ChevronUp,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { aiAssistantService } from "@/services/aiAssistantService";
import { Tables } from "@/types/database.types";
import ImageUpload from "@/components/common/ImageUpload";
import { FileUpload } from "@/components/ui/FileUpload";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAdminStatus } from "@/hooks/auth/useAdminStatus";
import { supabase as supabaseClient } from "@/lib/supabase";

type AIAssistant = Tables<"ai_assistants">;

// Form field type for the form fields array
const formFieldSchema = z
  .object({
    label: z.string().min(1, "Field label is required"),
    type: z.enum([
      "text",
      "textarea",
      "select",
      "number",
      "email",
      "url",
      "file",
      "image",
    ]),
    required: z.boolean(),
    placeholder: z.string().optional(),
    description: z.string().optional(),
    options: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    // Validate that select fields have options
    if (
      data.type === "select" &&
      (!data.options || data.options.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Options are required for select fields",
        path: ["options"],
      });
    }
  });

// Available AI models
const AI_MODELS = [
  {
    value: "anthropic/claude-sonnet-4-20250514",
    label: "Claude Sonnet 4 (Latest)",
  },
  { value: "anthropic/claude-3-7-sonnet-20250219", label: "Claude 3.7 Sonnet" },
  { value: "google/gemini-2.5-flash", label: "Gemini 2.5 Flash" },
  { value: "google/gemini-2.5-flash-image-preview", label: "Image generation" },
  { value: "xai/grok-4", label: "Grok 4" },
  { value: "openai/gpt-5", label: "GPT-5" },
  { value: "openai/gpt-4.1", label: "GPT-4.1" },
  { value: "openai/gpt-4o", label: "GPT-4o" },
  { value: "openai/o3", label: "o3" },
  { value: "openai/o4-mini", label: "o4 Mini" },
] as const;

// Utility function to extract provider from model string
const getProviderFromModel = (model: string): string | null => {
  const provider = model.split("/")[0];
  return ["anthropic", "openai", "xai", "google"].includes(provider)
    ? provider
    : null;
};

// Check if web search is supported for a given model
const isWebSearchSupported = (model: string): boolean => {
  return getProviderFromModel(model) !== null;
};

// Get provider display name and status
const getProviderInfo = (model: string) => {
  const provider = getProviderFromModel(model);
  if (!provider) {
    return {
      supported: false,
      message: "Web search not supported with this model",
    };
  }

  const providerNames = {
    anthropic: "Claude",
    openai: "GPT",
    xai: "Grok",
  };

  return {
    supported: true,
    message: `Web search available with ${providerNames[provider as keyof typeof providerNames]}`,
  };
};

// Main form schema
const aiAssistantSchema = z.object({
  name: z.string().min(1, "Name is required"),
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  chat_welcome_message: z.string().optional(),
  category: z.enum([
    "Sales",
    "Marketing",
    "Daily work",
    "Customer Engagement",
    "Operations",
    "Legal",
    "Finance",
    "HR",
  ]),
  icon_url: z.string().url().optional().or(z.literal("")),
  banner: z.string().url().optional().or(z.literal("")),
  cta_label: z.string().optional().or(z.literal("")),
  base_instructions: z.string().min(1, "Instructions are required"),
  model: z.enum([
    "anthropic/claude-sonnet-4-20250514",
    "anthropic/claude-3-7-sonnet-20250219",
    "google/gemini-2.5-flash",
    "google/gemini-2.5-flash-image-preview",
    "xai/grok-4",
    "openai/gpt-5",
    "openai/gpt-4.1",
    "openai/gpt-4o",
    "openai/o3",
    "openai/o4-mini",
  ]),
  max_output_tokens: z.number().min(1).max(8192),
  temperature: z.number().min(0).max(1).optional(),
  web_search_enabled: z.boolean().default(false),
  supports_chat: z.boolean().optional(),
  generation_mode: z.enum(["object", "text"]).default("object"),
  form_fields: z.array(formFieldSchema),
  zod_schema: z
    .object({
      type: z.literal("object"),
      properties: z.record(z.any()),
      required: z.array(z.string()).optional(),
    })
    .optional(),
  // Super admin only: industry linkage
  industry_id: z
    .string()
    .uuid()
    .optional()
    .or(z.literal(""))
    .transform((v) => (v === "" ? undefined : v)),
});

type FormData = z.infer<typeof aiAssistantSchema>;

interface AIAssistantFormProps {
  initialData?: AIAssistant;
  onSubmit: (data: FormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isChatAssistant?: boolean;
}

const defaultFormField = {
  name: "",
  label: "", // This will be auto-generated if empty
  type: "text" as const,
  required: false,
  placeholder: "",
  description: "",
  options: [],
};

const defaultZodSchema = {
  type: "object" as const,
  properties: {
    output_content: {
      type: "string",
      description: "The main generated content",
    },
  },
  required: ["output_content"],
};

export const AIAssistantForm: React.FC<AIAssistantFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isChatAssistant: isChatAssistantProp,
}) => {
  const { isAdmin } = useAdminStatus();
  const [activeTab, setActiveTab] = useState("basic");
  const [isUploading, setIsUploading] = useState<"icon" | "banner" | null>(
    null,
  );
  const [openFieldIds, setOpenFieldIds] = useState<Record<string, boolean>>({});
  const [schemaVersion, setSchemaVersion] = useState(0);
  const { toast } = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(aiAssistantSchema),
    defaultValues: {
      name: initialData?.name || "",
      title: initialData?.title || "",
      description: initialData?.description || "",
      chat_welcome_message: (initialData as any)?.chat_welcome_message || "",
      category:
        (initialData?.category as
          | "Sales"
          | "Marketing"
          | "Daily work"
          | "Customer Engagement"
          | "Operations"
          | "Legal"
          | "Finance"
          | "HR") || "Marketing",
      icon_url: initialData?.icon_url || "",
      banner: (initialData as any)?.banner || "",
      cta_label: initialData?.cta_label || "Generate",
      base_instructions: initialData?.base_instructions || "",
      model: (initialData as any)?.model || "openai/gpt-5",
      max_output_tokens: (initialData as any)?.max_output_tokens || 512,
      temperature: (initialData as any)?.temperature ?? 0.2,
      web_search_enabled: (initialData as any)?.web_search_enabled || false,
      supports_chat: (initialData as any)?.supports_chat || false,
      generation_mode:
        ((initialData as any)?.generation_mode as any) || "object",
      form_fields: (initialData?.form_fields as any) || [defaultFormField],
      zod_schema: (initialData?.zod_schema as any) || defaultZodSchema,
      industry_id: (initialData as any)?.industry_id || "",
    },
  });

  const zodSchema = useWatch({ control: form.control, name: "zod_schema" });
  const generationMode = useWatch({
    control: form.control,
    name: "generation_mode",
  });
  const supportsChat = useWatch({
    control: form.control,
    name: "supports_chat" as any,
  });
  const isChatAssistant =
    isChatAssistantProp ?? (supportsChat === true || generationMode === "chat");
  const [tabsPortalEl, setTabsPortalEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const el = document.getElementById(
      "assistant-tabs-portal",
    ) as HTMLElement | null;
    setTabsPortalEl(el);
  }, []);
  const applySchemaUpdate = (updated: any) => {
    form.setValue("zod_schema", updated, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    setSchemaVersion((v) => v + 1);
  };

  // Reset form when initialData changes (important for editing)
  useEffect(() => {
    if (initialData) {
      form.reset({
        name: initialData.name || "",
        title: initialData.title || "",
        description: initialData.description || "",
        category:
          (initialData.category as
            | "Sales"
            | "Marketing"
            | "Daily work"
            | "Customer Engagement"
            | "Operations"
            | "Legal"
            | "Finance"
            | "HR") || "Marketing",
        icon_url: initialData.icon_url || "",
        banner: (initialData as any)?.banner || "",
        cta_label: initialData.cta_label || "Generate",
        base_instructions: initialData.base_instructions || "",
        model: (initialData as any)?.model || "openai/gpt-5",
        max_output_tokens: (initialData as any)?.max_output_tokens || 512,
        temperature: (initialData as any)?.temperature ?? 0.2,
        web_search_enabled: (initialData as any)?.web_search_enabled || false,
        supports_chat: (initialData as any)?.supports_chat || false,
        generation_mode:
          ((initialData as any)?.generation_mode as any) || "object",
        form_fields: (initialData.form_fields as any) || [defaultFormField],
        zod_schema: (initialData.zod_schema as any) || defaultZodSchema,
        industry_id: (initialData as any)?.industry_id || "",
      });
    }
  }, [initialData, form]);

  const {
    fields: formFields,
    append: appendFormField,
    remove: removeFormField,
    move: moveFormField,
  } = useFieldArray({
    control: form.control,
    name: "form_fields",
  });

  // Local UI state for select options text so Enter can add new lines without being trimmed immediately
  const [optionsTextByFieldId, setOptionsTextByFieldId] = useState<
    Record<string, string>
  >({});

  // Keep local state reasonably in sync when editing an existing assistant
  useEffect(() => {
    const next: Record<string, string> = {};
    formFields.forEach((f, idx) => {
      const current = form.getValues(`form_fields.${idx}.options` as const) as
        | string[]
        | undefined;
      if (current && current.length > 0) {
        next[f.id] = current.join("\n");
      }
    });
    setOptionsTextByFieldId((prev) => ({ ...next, ...prev }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formFields.length]);

  // Small helper component to subscribe to a field value and display it
  const WatchedLabel = ({ name }: { name: string }) => {
    const value = useWatch({ control: form.control, name });
    return <span className="font-medium">{value || "Untitled"}</span>;
  };

  const handleSubmit = (data: FormData) => {
    console.log("Form submission started with data:", data);

    // Clean up empty strings and set to null for database
    const cleanedData = {
      ...data,
      icon_url: data.icon_url || null,
      banner: data.banner || null,
      industry_id: isAdmin && data.industry_id ? data.industry_id : undefined,
    };

    console.log("Cleaned data for submission:", cleanedData);
    onSubmit(cleanedData);
  };

  const handleSubmitError = (errors: any) => {
    console.error("Form validation errors:", errors);

    // Show toast with validation errors
    const errorMessages = Object.entries(errors)
      .map(
        ([field, error]: [string, any]) =>
          `${field}: ${error.message || "Invalid value"}`,
      )
      .join(", ");

    toast({
      title: "Form Validation Error",
      description: `Please fix the following issues: ${errorMessages}`,
      variant: "destructive",
    });

    // Find the first tab with errors and switch to it
    const tabsWithErrors = {
      basic: [
        "name",
        "title",
        "description",
        "category",
        "icon_url",
        "banner",
        "cta_label",
      ],
      instructions: ["base_instructions"],
      model: ["model", "max_output_tokens", "web_search_enabled"],
      form: ["form_fields"],
      schema: ["zod_schema"],
    };

    for (const [tab, fields] of Object.entries(tabsWithErrors)) {
      if (fields.some((field) => errors[field])) {
        setActiveTab(tab);
        break;
      }
    }
  };

  // Super-admin editor for base generator links
  function LinkedGeneratorsEditor({ assistantId }: { assistantId?: string }) {
    const [allGenerators, setAllGenerators] = useState<
      Array<{ id: string; title: string; icon_url: string | null }>
    >([]);
    const [linkedIds, setLinkedIds] = useState<Set<string>>(new Set());
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      const load = async () => {
        if (!assistantId) return;
        // Fetch all generator assistants (base) created by super-admin (no org) for super-admin to pick from
        const { data: gens } = await supabase
          .from("ai_assistants")
          .select(
            "id, title, icon_url, supports_chat, is_active, owner_organization_id",
          )
          .eq("supports_chat", false)
          .eq("is_active", true)
          .is("owner_organization_id", null);
        setAllGenerators(
          (gens || []).map((g: any) => ({
            id: g.id,
            title: g.title,
            icon_url: g.icon_url,
          })),
        );

        // Fetch existing links for this chat assistant
        const { data: links } = await supabase
          .from("assistant_generators")
          .select("generator_assistant_id")
          .eq("chat_assistant_id", assistantId);
        setLinkedIds(
          new Set(
            ((links || []) as any[]).map(
              (l) => l.generator_assistant_id as string,
            ),
          ),
        );
      };
      load();
    }, [assistantId]);

    const toggle = (id: string) => {
      setLinkedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    };

    const save = async () => {
      if (!assistantId) return;
      setIsSaving(true);
      try {
        // Read current links again to compute diff safely
        const { data: current } = await supabase
          .from("assistant_generators")
          .select("generator_assistant_id")
          .eq("chat_assistant_id", assistantId);
        const currentSet = new Set(
          ((current || []) as any[]).map(
            (l) => l.generator_assistant_id as string,
          ),
        );

        const desired = linkedIds;
        const toInsert: string[] = [];
        const toDelete: string[] = [];
        for (const id of desired) if (!currentSet.has(id)) toInsert.push(id);
        for (const id of currentSet) if (!desired.has(id)) toDelete.push(id);

        if (toInsert.length > 0) {
          await supabase.from("assistant_generators").insert(
            toInsert.map((genId) => ({
              chat_assistant_id: assistantId,
              generator_assistant_id: genId,
            })),
          );
        }
        if (toDelete.length > 0) {
          await supabase
            .from("assistant_generators")
            .delete()
            .eq("chat_assistant_id", assistantId)
            .in("generator_assistant_id", toDelete);
        }
        toast({ title: "Saved", description: "AI tool links updated." });
      } catch (e: any) {
        console.error("Failed to save generator links", e);
        toast({
          title: "Save failed",
          description: e?.message || String(e),
          variant: "destructive",
        });
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
          {allGenerators.map((g) => {
            const selected = linkedIds.has(g.id);
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => toggle(g.id)}
                className={`flex items-center gap-2 rounded border px-3 py-2 text-sm text-left ${selected ? "bg-green-50 border-green-300" : "bg-gray-50 border-gray-200"}`}
              >
                <span className="inline-block h-6 w-6 rounded bg-white border overflow-hidden flex items-center justify-center">
                  {g.icon_url ? (
                    <img
                      src={g.icon_url}
                      alt={g.title}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    "🤖"
                  )}
                </span>
                <span className="truncate">{g.title}</span>
                <span
                  className={`ml-auto inline-block h-2 w-2 rounded-full ${selected ? "bg-green-600" : "bg-gray-400"}`}
                />
              </button>
            );
          })}
          {allGenerators.length === 0 && (
            <div className="text-sm text-gray-500">No AI tools found.</div>
          )}
        </div>
        <div className="mt-3 flex justify-end">
          <Button type="button" onClick={save} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save links"}
          </Button>
        </div>
      </div>
    );
  }

  // Helper to check if a tab has errors
  const getTabErrors = (tabName: string) => {
    const tabFields = {
      basic: [
        "name",
        "title",
        "description",
        "category",
        "icon_url",
        "banner",
        "cta_label",
      ],
      instructions: ["base_instructions"],
      model: ["model", "max_output_tokens", "web_search_enabled"],
      form: ["form_fields"],
      schema: ["zod_schema"],
    };

    const fields = tabFields[tabName as keyof typeof tabFields] || [];
    return fields.some(
      (field) =>
        form.formState.errors[field as keyof typeof form.formState.errors],
    );
  };

  const handleCancel = () => {
    // Reset form to initial state when canceling
    if (initialData) {
      form.reset({
        name: initialData.name || "",
        title: initialData.title || "",
        description: initialData.description || "",
        category:
          (initialData.category as
            | "Sales"
            | "Marketing"
            | "Daily work"
            | "Customer Engagement"
            | "Operations"
            | "Legal"
            | "Finance"
            | "HR") || "Marketing",
        icon_url: initialData.icon_url || "",
        banner: (initialData as any)?.banner || "",
        cta_label: initialData.cta_label || "Generate",
        base_instructions: initialData.base_instructions || "",
        model: (initialData as any)?.model || "openai/gpt-5",
        temperature: (initialData as any)?.temperature || 0.3,
        max_output_tokens: (initialData as any)?.max_output_tokens || 512,
        web_search_enabled: (initialData as any)?.web_search_enabled || false,
        supports_chat: (initialData as any)?.supports_chat || false,
        generation_mode:
          ((initialData as any)?.generation_mode as any) || "object",
        form_fields: (initialData.form_fields as any) || [defaultFormField],
        zod_schema: (initialData.zod_schema as any) || defaultZodSchema,
        industry_id: (initialData as any)?.industry_id || "",
      });
    } else {
      // For create mode, reset to empty form
      form.reset();
    }
    onCancel();
  };

  const addFormField = () => {
    appendFormField(defaultFormField);
  };

  const moveFieldUp = (index: number) => {
    if (index > 0) {
      moveFormField(index, index - 1);
    }
  };

  const moveFieldDown = (index: number) => {
    if (index < formFields.length - 1) {
      moveFormField(index, index + 1);
    }
  };

  // Handle file uploads for icon and banner
  const handleFileUpload = async (fileType: "icon" | "banner", file: File) => {
    setIsUploading(fileType);

    try {
      // Validate the file
      const maxSize = 5; // 5MB max for all files

      // Validate file type with specific MIME types
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/svg+xml",
        "image/webp",
      ];

      if (!allowedTypes.includes(file.type)) {
        throw new Error(
          "Invalid file type. Please upload JPG, PNG, GIF, SVG, or WebP images.",
        );
      }

      if (file.size > maxSize * 1024 * 1024) {
        throw new Error(`File size exceeds ${maxSize}MB limit.`);
      }

      // Upload the file to storage with safe file extension handling
      const fileExt = file.name.split(".").pop()?.toLowerCase();
      const validExtensions = ["jpg", "jpeg", "png", "gif", "svg", "webp"];
      const safeExtension =
        fileExt && validExtensions.includes(fileExt) ? fileExt : "jpg";
      const fileName = `assistant-${fileType}-${Date.now()}.${safeExtension}`;

      // Use the correct storage paths based on the fileType
      const storagePath =
        fileType === "icon"
          ? `assistant_assets/assistant_icons/${fileName}`
          : `assistant_assets/assistant_banners/${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("image_library")
        .upload(storagePath, file, {
          upsert: true,
          contentType: file.type,
        });

      if (uploadError) {
        console.error("Storage upload error:", uploadError);
        throw new Error(`Error uploading image: ${uploadError.message}`);
      }

      // Get the public URL
      const { data: publicUrlData } = supabase.storage
        .from("image_library")
        .getPublicUrl(storagePath);

      if (!publicUrlData || !publicUrlData.publicUrl) {
        throw new Error("Failed to get public URL for uploaded file");
      }

      const fileUrl = publicUrlData.publicUrl;

      // Update the form with the new URL
      if (fileType === "icon") {
        form.setValue("icon_url", fileUrl);
      } else {
        form.setValue("banner", fileUrl);
      }

      toast({
        title: `${fileType.charAt(0).toUpperCase() + fileType.slice(1)} updated`,
        description: `Your ${fileType} has been uploaded successfully.`,
      });
    } catch (error: any) {
      console.error(`${fileType} upload error:`, error);
      toast({
        variant: "destructive",
        title: `Failed to upload ${fileType}`,
        description: error.message || "An unexpected error occurred.",
      });
    } finally {
      setIsUploading(null);
    }
  };

  const handleIconUpload = (file: File) => {
    // Check if it's a removal (dummy file)
    if (file.name === "empty" && file.type === "text/plain") {
      form.setValue("icon_url", "");
      return;
    }
    handleFileUpload("icon", file);
  };

  const handleBannerUpload = (file: File) => {
    // Check if it's a removal (dummy file)
    if (file.name === "empty" && file.type === "text/plain") {
      form.setValue("banner", "");
      return;
    }
    handleFileUpload("banner", file);
  };

  const addZodProperty = (parentPathArg: unknown = "") => {
    const parentPath = typeof parentPathArg === "string" ? parentPathArg : "";
    const currentSchema = form.getValues("zod_schema");
    let targetProperties, newPropertyName;

    if (parentPath === "") {
      // Root level property
      targetProperties = currentSchema.properties;
      newPropertyName = `property_${Object.keys(targetProperties).length + 1}`;
    } else {
      // Nested property - traverse to the correct location
      const pathParts = parentPath.split(".");
      targetProperties = pathParts.reduce((props, part) => {
        return props[part]?.properties || {};
      }, currentSchema.properties);

      // Better naming for nested properties based on context
      const existingKeys = Object.keys(targetProperties);
      if (existingKeys.length === 0) {
        newPropertyName = "option1";
      } else if (existingKeys.length === 1 && existingKeys[0] === "option1") {
        newPropertyName = "option2";
      } else {
        // For additional options, use sequential naming
        let optionNumber = existingKeys.length + 1;
        newPropertyName = `option${optionNumber}`;

        // Ensure unique naming
        while (targetProperties[newPropertyName]) {
          optionNumber++;
          newPropertyName = `option${optionNumber}`;
        }
      }
    }

    const updatedSchema = JSON.parse(JSON.stringify(currentSchema)); // Deep clone

    if (parentPath === "") {
      updatedSchema.properties[newPropertyName] = {
        type: "string",
        description: "New property description",
      };
    } else {
      // Navigate to nested location and add property
      const pathParts = parentPath.split(".");
      let current = updatedSchema.properties;

      for (const part of pathParts) {
        if (!current[part].properties) {
          current[part].properties = {};
        }
        current = current[part].properties;
      }

      current[newPropertyName] = {
        type: "string",
        description: "Option description",
      };
    }

    applySchemaUpdate(updatedSchema);
  };

  const removeZodProperty = (propertyName: string, parentPath = "") => {
    const currentSchema = form.getValues("zod_schema");
    const updatedSchema = JSON.parse(JSON.stringify(currentSchema)); // Deep clone

    if (parentPath === "") {
      // Remove root level property
      delete updatedSchema.properties[propertyName];
      updatedSchema.required =
        updatedSchema.required?.filter((req) => req !== propertyName) || [];
    } else {
      // Remove nested property
      const pathParts = parentPath.split(".");
      let current = updatedSchema.properties;
      for (const part of pathParts) {
        current = current[part].properties;
      }
      delete current[propertyName];

      // If parent container now has no sub-blocks, turn it back into a string
      const parentPropsPath = pathParts.slice(0, -1);
      let parentPropsRef = updatedSchema.properties;
      for (const part of parentPropsPath) {
        parentPropsRef = parentPropsRef[part].properties;
      }
      const parentKey = pathParts[pathParts.length - 1];
      const parentContainer = parentPropsRef[parentKey];
      if (
        parentContainer &&
        parentContainer.type === "object" &&
        (!parentContainer.properties ||
          Object.keys(parentContainer.properties).length === 0)
      ) {
        parentContainer.type = "string";
        delete parentContainer.properties;
      }
    }

    applySchemaUpdate(updatedSchema);
  };

  const moveZodProperty = (
    propertyName: string,
    direction: "up" | "down",
    parentPath = "",
  ) => {
    const currentSchema = form.getValues("zod_schema");
    const updatedSchema = JSON.parse(JSON.stringify(currentSchema)); // Deep clone

    let targetProperties;
    if (parentPath === "") {
      targetProperties = updatedSchema.properties;
    } else {
      const pathParts = parentPath.split(".");
      let current = updatedSchema.properties;
      for (const part of pathParts) {
        current = current[part].properties;
      }
      targetProperties = current;
    }

    const keys = Object.keys(targetProperties);
    const currentIndex = keys.indexOf(propertyName);

    if (currentIndex === -1) return;

    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= keys.length) return;

    // Reorder the keys array
    const newKeys = [...keys];
    [newKeys[currentIndex], newKeys[newIndex]] = [
      newKeys[newIndex],
      newKeys[currentIndex],
    ];

    // Rebuild the properties object in the new order
    const reorderedProperties = {};
    newKeys.forEach((key) => {
      reorderedProperties[key] = targetProperties[key];
    });

    if (parentPath === "") {
      updatedSchema.properties = reorderedProperties;
    } else {
      const pathParts = parentPath.split(".");
      let current = updatedSchema.properties;
      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current[pathParts[i]].properties;
      }
      current[pathParts[pathParts.length - 1]].properties = reorderedProperties;
    }

    applySchemaUpdate(updatedSchema);
  };

  const toggleZodPropertyType = (propertyName: string, parentPath = "") => {
    const currentSchema = form.getValues("zod_schema");
    const updatedSchema = JSON.parse(JSON.stringify(currentSchema)); // Deep clone

    let targetProperty;
    if (parentPath === "") {
      targetProperty = updatedSchema.properties[propertyName];
    } else {
      const pathParts = parentPath.split(".");
      let current = updatedSchema.properties;
      for (const part of pathParts) {
        current = current[part].properties;
      }
      targetProperty = current[propertyName];
    }

    if (targetProperty.type === "object") {
      // Convert from object to string
      targetProperty.type = "string";
      delete targetProperty.properties;
    } else {
      // Convert to object
      targetProperty.type = "object";
      targetProperty.properties = {};
    }

    applySchemaUpdate(updatedSchema);
  };

  const updateZodPropertyName = (
    oldName: string,
    newName: string,
    parentPath = "",
  ) => {
    const currentSchema = form.getValues("zod_schema");
    const updatedSchema = JSON.parse(JSON.stringify(currentSchema));

    if (parentPath === "") {
      // Preserve order by rebuilding the properties object
      const oldConfig = updatedSchema.properties[oldName];
      const newProperties = {};

      // Rebuild properties in the same order, replacing the old name with new name
      Object.keys(updatedSchema.properties).forEach((key) => {
        if (key === oldName) {
          newProperties[newName] = oldConfig;
        } else {
          newProperties[key] = updatedSchema.properties[key];
        }
      });

      updatedSchema.properties = newProperties;
      updatedSchema.required =
        updatedSchema.required?.map((req) =>
          req === oldName ? newName : req,
        ) || [];
    } else {
      // Handle nested properties
      const pathParts = parentPath.split(".");
      let current = updatedSchema.properties;

      // Navigate to the parent container
      for (const part of pathParts) {
        current = current[part].properties;
      }

      // Preserve order for nested properties
      const oldConfig = current[oldName];
      const newProperties = {};

      Object.keys(current).forEach((key) => {
        if (key === oldName) {
          newProperties[newName] = oldConfig;
        } else {
          newProperties[key] = current[key];
        }
      });

      // Replace the properties object while preserving order
      Object.keys(current).forEach((key) => delete current[key]);
      Object.assign(current, newProperties);
    }

    applySchemaUpdate(updatedSchema);
  };

  const updateZodPropertyDescription = (
    propertyName: string,
    description: string,
    parentPath = "",
  ) => {
    const currentSchema = form.getValues("zod_schema");
    const updatedSchema = JSON.parse(JSON.stringify(currentSchema));

    if (parentPath === "") {
      updatedSchema.properties[propertyName] = {
        ...updatedSchema.properties[propertyName],
        description: description,
      };
    } else {
      const pathParts = parentPath.split(".");
      let current = updatedSchema.properties;
      for (const part of pathParts) {
        current = current[part].properties;
      }
      current[propertyName] = {
        ...current[propertyName],
        description: description,
      };
    }

    applySchemaUpdate(updatedSchema);
  };

  // Recursive component to render properties with nesting support
  const PropertyRenderer = ({
    properties,
    requiredNames,
    parentPath = "",
    level = 0,
  }: {
    properties: any;
    requiredNames: string[];
    parentPath?: string;
    level?: number;
  }) => {
    const maxLevel = 2; // Allow up to 3 levels (0, 1, 2)
    const propertyEntries = Object.entries(properties || {});

    return (
      <div
        className={`space-y-4 ${level > 0 ? "ml-6 pl-4 border-l-2 border-muted" : ""}`}
      >
        {propertyEntries.map(
          ([propertyName, propertyConfig]: [string, any], index) => {
            const currentPath = parentPath
              ? `${parentPath}.${propertyName}`
              : propertyName;
            const isObject = propertyConfig.type === "object";
            const isFirst = index === 0;
            const isLast = index === propertyEntries.length - 1;

            // Create stable key that doesn't change when property name changes
            const stableKey = `${parentPath}-${index}-${level}`;

            const isRootRequired = requiredNames?.includes(propertyName);
            return (
              <PropertyField
                key={stableKey}
                propertyName={propertyName}
                propertyConfig={propertyConfig}
                parentPath={parentPath}
                currentPath={currentPath}
                isObject={isObject}
                isFirst={isFirst}
                isLast={isLast}
                level={level}
                maxLevel={maxLevel}
                isRequired={parentPath === "" ? !!isRootRequired : false}
                onUpdateName={updateZodPropertyName}
                onUpdateDescription={updateZodPropertyDescription}
                onToggleType={toggleZodPropertyType}
                onMove={moveZodProperty}
                onAdd={addZodProperty}
                onRemove={removeZodProperty}
                onToggleRequired={toggleZodRequired}
                form={form}
              />
            );
          },
        )}
      </div>
    );
  };

  // Individual property field component with local state
  const PropertyField = ({
    propertyName,
    propertyConfig,
    parentPath,
    currentPath,
    isObject,
    isFirst,
    isLast,
    level,
    maxLevel,
    onUpdateName,
    onUpdateDescription,
    onToggleType,
    onMove,
    onAdd,
    onRemove,
    onToggleRequired,
    form,
    isRequired,
  }: {
    propertyName: string;
    propertyConfig: any;
    parentPath: string;
    currentPath: string;
    isObject: boolean;
    isFirst: boolean;
    isLast: boolean;
    level: number;
    maxLevel: number;
    onUpdateName: (
      oldName: string,
      newName: string,
      parentPath: string,
    ) => void;
    onUpdateDescription: (
      propertyName: string,
      description: string,
      parentPath: string,
    ) => void;
    onToggleType: (propertyName: string, parentPath: string) => void;
    onMove: (
      propertyName: string,
      direction: "up" | "down",
      parentPath: string,
    ) => void;
    onAdd: (parentPath: string) => void;
    onRemove: (propertyName: string, parentPath: string) => void;
    onToggleRequired: (propertyName: string) => void;
    form: any;
    isRequired: boolean;
  }) => {
    const [localName, setLocalName] = React.useState(propertyName);
    const [localDescription, setLocalDescription] = React.useState(
      propertyConfig.description || "",
    );

    // Update local state when props change
    React.useEffect(() => {
      setLocalName(propertyName);
    }, [propertyName]);

    React.useEffect(() => {
      setLocalDescription(propertyConfig.description || "");
    }, [propertyConfig.description]);

    const handleNameBlur = () => {
      // Trim whitespace and only update if there's a meaningful change
      const trimmedLocalName = localName.trim();
      const trimmedPropertyName = propertyName.trim();

      if (
        trimmedLocalName &&
        trimmedLocalName !== trimmedPropertyName &&
        trimmedLocalName !== propertyName
      ) {
        onUpdateName(propertyName, trimmedLocalName, parentPath);
      }
    };

    const handleDescriptionBlur = () => {
      if (localDescription !== (propertyConfig.description || "")) {
        onUpdateDescription(propertyName, localDescription, parentPath);
      }
    };

    return (
      <div className="space-y-2">
        {/* Property Header */}
        <div className="flex items-center gap-4 p-4 border rounded-lg bg-background">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Input
                value={localName}
                onChange={(e) => setLocalName(e.target.value)}
                onBlur={handleNameBlur}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.currentTarget.blur();
                  }
                }}
                className="font-mono text-sm max-w-xs"
              />

              {/* Type Toggle Button (renamed to Block/Text) */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onToggleType(propertyName, parentPath)}
                className={`text-xs ${
                  isObject
                    ? "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                    : "hover:bg-gray-50"
                }`}
                title={isObject ? "Convert to Text" : "Split into sub-blocks"}
              >
                {isObject ? "🧩 Block" : "📝 Text"}
              </Button>

              {parentPath === "" && (
                <div className="flex items-center gap-2">
                  <Switch
                    checked={isRequired}
                    onCheckedChange={() => onToggleRequired(propertyName)}
                  />
                  <span className="text-xs">
                    {isRequired ? "Required" : "Optional"}
                  </span>
                </div>
              )}
            </div>

            <Input
              placeholder="Property description"
              value={localDescription}
              onChange={(e) => setLocalDescription(e.target.value)}
              onBlur={handleDescriptionBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.currentTarget.blur();
                }
              }}
              className="text-sm"
            />
          </div>

          <div className="flex gap-2">
            {/* Reorder Buttons */}
            <div className="flex flex-col gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onMove(propertyName, "up", parentPath)}
                disabled={isFirst}
                className="h-6 w-6 p-0"
              >
                <ChevronUp className="h-3 w-3" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onMove(propertyName, "down", parentPath)}
                disabled={isLast}
                className="h-6 w-6 p-0"
              >
                <ChevronDown className="h-3 w-3" />
              </Button>
            </div>

            {/* Add Nested Property Button (only for objects and within level limit) */}
            {isObject && level < maxLevel && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onAdd(currentPath || "")}
                title="Add sub-block"
                className="text-green-700 hover:text-green-800"
              >
                + Add sub-block
              </Button>
            )}

            {/* Remove Property Button */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onRemove(propertyName, parentPath)}
              title="Remove this property"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Helper message for object types */}
        {isObject &&
          (!propertyConfig.properties ||
            Object.keys(propertyConfig.properties).length === 0) && (
            <div className="mx-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4 text-green-600" />
                <span>
                  This is an <strong>Object</strong> property. Click the green{" "}
                  <strong>+</strong> button above to add nested properties like
                  "option1", "option2", etc.
                </span>
              </div>
            </div>
          )}

        {/* Nested Properties (recursive) */}
        {isObject && propertyConfig.properties && (
          <div className="border-l-2 border-blue-200 ml-4">
            <PropertyRenderer
              properties={propertyConfig.properties}
              parentPath={currentPath}
              level={level + 1}
            />
          </div>
        )}
      </div>
    );
  };

  const toggleZodRequired = (propertyName: string) => {
    const currentSchema = form.getValues("zod_schema");
    const currentRequired = currentSchema.required || [];
    const isRequired = currentRequired.includes(propertyName);

    const updatedSchema = {
      ...currentSchema,
      required: isRequired
        ? currentRequired.filter((req) => req !== propertyName)
        : [...currentRequired, propertyName],
    };

    applySchemaUpdate(updatedSchema);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit, handleSubmitError)}
        className="space-y-6"
      >
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {(() => {
            const visibleTabs = [
              "basic",
              "ai",
              ...(isChatAssistant ? [] : ["form", "schema"]),
            ];
            const isSuperAdmin = isAdmin;
            const tabsList = (
              <TabsList
                className={`grid w-full ${isChatAssistant ? "grid-cols-2" : "grid-cols-4"}`}
              >
                <TabsTrigger
                  value="basic"
                  className={
                    getTabErrors("basic") ? "border-red-500 text-red-600" : ""
                  }
                >
                  Basic Info {getTabErrors("basic") && "⚠️"}
                </TabsTrigger>
                <TabsTrigger
                  value="ai"
                  className={
                    getTabErrors("ai") ? "border-red-500 text-red-600" : ""
                  }
                >
                  AI settings {isSuperAdmin ? "(Super Admin) " : ""}
                  {getTabErrors("ai") && "⚠️"}
                </TabsTrigger>
                {!isChatAssistant && (
                  <TabsTrigger
                    value="form"
                    className={
                      getTabErrors("form") ? "border-red-500 text-red-600" : ""
                    }
                  >
                    Inputs {getTabErrors("form") && "⚠️"}
                  </TabsTrigger>
                )}
                {isSuperAdmin && !isChatAssistant && (
                  <TabsTrigger
                    value="schema"
                    className={
                      getTabErrors("schema")
                        ? "border-red-500 text-red-600"
                        : ""
                    }
                  >
                    Output formatting (Super Admin){" "}
                    {getTabErrors("schema") && "⚠️"}
                  </TabsTrigger>
                )}
              </TabsList>
            );
            return tabsPortalEl
              ? createPortal(tabsList, tabsPortalEl)
              : tabsList;
          })()}

          <TabsContent value="basic" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {isAdmin && (
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name (Super Admin)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="internal-assistant-name"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Internal identifier. Shown only to Super Admins.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title*</FormLabel>
                    <FormControl>
                      <Input placeholder="LinkedIn Posts Writer" {...field} />
                    </FormControl>
                    <FormDescription>Display title for users</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {isChatAssistant && isAdmin && (
              <div className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Linked AI tools</CardTitle>
                    <CardDescription>
                      Choose which AI tools this AI assistant can use. Applies
                      to all its instances.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <LinkedGeneratorsEditor assistantId={initialData?.id} />
                  </CardContent>
                </Card>
              </div>
            )}

            {isAdmin && !isChatAssistant && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="industry_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Industry (Super Admin)</FormLabel>
                      <IndustrySelect
                        value={field.value || ""}
                        onChange={field.onChange}
                      />
                      <FormDescription>
                        Link this assistant to an industry for discovery
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description*</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Generate engaging LinkedIn posts that go viral and boost your professional presence"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>User-facing description</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isChatAssistant && (
              <FormField
                control={form.control}
                name="chat_welcome_message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Chat Welcome Message</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Hi! I'm here to help you with your tasks. What can I do for you today?"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Welcome message shown when starting a new chat
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category*</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Sales">Sales</SelectItem>
                        <SelectItem value="Marketing">Marketing</SelectItem>
                        <SelectItem value="Daily work">Daily work</SelectItem>
                        <SelectItem value="Customer Engagement">
                          Customer Engagement
                        </SelectItem>
                        <SelectItem value="Operations">Operations</SelectItem>
                        <SelectItem value="Legal">Legal</SelectItem>
                        <SelectItem value="Finance">Finance</SelectItem>
                        <SelectItem value="HR">HR</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>Assistant category</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {!isChatAssistant && (
                <FormField
                  control={form.control}
                  name="cta_label"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CTA Label*</FormLabel>
                      <FormControl>
                        <Input placeholder="Generate" {...field} />
                      </FormControl>
                      <FormDescription>
                        Label for the primary action button
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              <FormField
                control={form.control}
                name="icon_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Assistant Icon</FormLabel>
                    <FormControl>
                      <ImageUpload
                        onFileSelect={handleIconUpload}
                        previewUrl={field.value || null}
                        maxSize={5}
                        className="h-40 w-40 rounded-lg overflow-hidden"
                        aspectRatio="1:1"
                      />
                    </FormControl>
                    <FormDescription>
                      Recommended size: 512x512px. Max file size: 5MB.
                      {isUploading === "icon" && (
                        <span className="text-blue-600 ml-2">Uploading...</span>
                      )}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="banner"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Banner Image</FormLabel>
                    <FormControl>
                      <ImageUpload
                        onFileSelect={handleBannerUpload}
                        previewUrl={field.value || null}
                        maxSize={5}
                        className="h-32 w-full"
                        aspectRatio="3:1"
                      />
                    </FormControl>
                    <FormDescription>
                      Recommended size: 1200x400px. Max file size: 5MB.
                      {isUploading === "banner" && (
                        <span className="text-blue-600 ml-2">Uploading...</span>
                      )}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </TabsContent>

          <TabsContent value="ai" className="space-y-4">
            <FormField
              control={form.control}
              name="base_instructions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Base Instructions*</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="You are an expert LinkedIn content creator..."
                      rows={20}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    System prompt and instructions for the AI assistant
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="h-2" />
            {isAdmin && (
              <FormField
                control={form.control}
                name="model"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>AI Model* (Super Admin)</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select AI model" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {AI_MODELS.map((model) => (
                          <SelectItem key={model.value} value={model.value}>
                            {model.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Choose the AI model to use for content generation
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              {isAdmin && (
                <FormField
                  control={form.control}
                  name="max_output_tokens"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Max Output Tokens* (Super Admin)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          max="8192"
                          placeholder="512"
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseInt(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        Maximum length of generated content
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              {isAdmin && (
                <FormField
                  control={form.control}
                  name="temperature"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Temperature (Super Admin)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          max="1"
                          step="0.01"
                          placeholder="0.2"
                          value={
                            typeof field.value === "number" ? field.value : 0.2
                          }
                          onChange={(e) =>
                            field.onChange(parseFloat(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        Controls randomness (0 = deterministic, 1 = very
                        creative)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <FormField
              control={form.control}
              name="web_search_enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Web Search</FormLabel>
                    <FormDescription>
                      {(() => {
                        const currentModel = form.watch("model");
                        const providerInfo = getProviderInfo(
                          currentModel || "",
                        );
                        return (
                          <span
                            className={
                              providerInfo.supported
                                ? "text-green-600"
                                : "text-orange-600"
                            }
                          >
                            {providerInfo.message}
                          </span>
                        );
                      })()}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={field.value || false}
                        onChange={(e) => {
                          const currentModel = form.watch("model");
                          const isSupported = isWebSearchSupported(
                            currentModel || "",
                          );
                          if (isSupported) {
                            field.onChange(e.target.checked);
                          }
                        }}
                        disabled={
                          !isWebSearchSupported(form.watch("model") || "")
                        }
                      />
                      <div
                        className={`
                        relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out
                        ${
                          field.value &&
                          isWebSearchSupported(form.watch("model") || "")
                            ? "bg-blue-600"
                            : "bg-gray-200"
                        }
                        ${!isWebSearchSupported(form.watch("model") || "") ? "opacity-50 cursor-not-allowed" : ""}
                      `}
                      >
                        <div
                          className={`
                          absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform duration-200 ease-in-out
                          ${field.value && isWebSearchSupported(form.watch("model") || "") ? "translate-x-5" : "translate-x-0"}
                        `}
                        />
                      </div>
                    </label>
                  </FormControl>
                </FormItem>
              )}
            />

            {isAdmin && (
              <FormField
                control={form.control}
                name="supports_chat"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Supports Chat (Super Admin)
                      </FormLabel>
                      <FormDescription>
                        Enable this assistant to be used in chat mode with
                        conversation history
                      </FormDescription>
                    </div>
                    <FormControl>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={field.value || false}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                        <div
                          className={`
                          relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out
                          ${field.value ? "bg-blue-600" : "bg-gray-200"}
                        `}
                        >
                          <div
                            className={`
                            absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform duration-200 ease-in-out
                            ${field.value ? "translate-x-5" : "translate-x-0"}
                          `}
                          />
                        </div>
                      </label>
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
          </TabsContent>

          {!isChatAssistant && (
            <TabsContent value="form" className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-medium">Form Fields</h3>
                </div>
                <Button type="button" onClick={addFormField} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Field
                </Button>
              </div>

              <div className="space-y-4 pl-10">
                {formFields.map((field, index) => (
                  <div key={field.id} className="relative">
                    {/* Left reorder controls, outside the card */}
                    <div className="absolute -left-10 top-4 flex flex-col gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => moveFieldUp(index)}
                        disabled={index === 0}
                        className="h-6 w-6 p-0"
                        title="Move up"
                      >
                        <ChevronUp className="h-3 w-3" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => moveFieldDown(index)}
                        disabled={index === formFields.length - 1}
                        className="h-6 w-6 p-0"
                        title="Move down"
                      >
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </div>
                    <Card className="p-4">
                      {/* Collapsed header */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            aria-label={
                              ((openFieldIds as any)?.[field.id]
                                ? "Collapse"
                                : "Expand") as string
                            }
                            className="h-6 w-6 flex items-center justify-center rounded hover:bg-muted"
                            onClick={() =>
                              setOpenFieldIds((s) => ({
                                ...s,
                                [field.id]: !(s as any)?.[field.id],
                              }))
                            }
                          >
                            {(openFieldIds as any)?.[field.id] ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                          {/* Removed inner reorder buttons per UX request */}
                          <WatchedLabel name={`form_fields.${index}.label`} />
                          <span className="flex items-center gap-2 text-sm">
                            <Switch
                              checked={
                                !!form.watch(`form_fields.${index}.required`)
                              }
                              onCheckedChange={(val) =>
                                form.setValue(
                                  `form_fields.${index}.required` as any,
                                  val,
                                )
                              }
                            />
                            Required
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeFormField(index)}
                            className="text-red-600 hover:text-red-700"
                            title="Remove"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {(openFieldIds as any)?.[field.id] && (
                        <div className="mt-4 flex items-start justify-between gap-3">
                          <div className="flex flex-col gap-3 w-full">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <FormField
                                control={form.control}
                                name={`form_fields.${index}.label`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Label</FormLabel>
                                    <FormControl>
                                      <Input placeholder="Label" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`form_fields.${index}.type`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Type</FormLabel>
                                    <Select
                                      onValueChange={field.onChange}
                                      defaultValue={field.value}
                                    >
                                      <FormControl>
                                        <SelectTrigger className="md:max-w-[200px]">
                                          <SelectValue />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="text">
                                          Text
                                        </SelectItem>
                                        <SelectItem value="textarea">
                                          Textarea
                                        </SelectItem>
                                        <SelectItem value="select">
                                          Select
                                        </SelectItem>
                                        <SelectItem value="number">
                                          Number
                                        </SelectItem>
                                        <SelectItem value="email">
                                          Email
                                        </SelectItem>
                                        <SelectItem value="url">URL</SelectItem>
                                        <SelectItem value="file">
                                          File
                                        </SelectItem>
                                        <SelectItem value="image">
                                          Image
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <div className="pt-6" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <FormField
                                control={form.control}
                                name={`form_fields.${index}.placeholder`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Placeholder</FormLabel>
                                    <FormControl>
                                      <Input
                                        placeholder="Enter your topic..."
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`form_fields.${index}.description`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Description</FormLabel>
                                    <FormControl>
                                      <Input
                                        placeholder="Help text for this field"
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            {form.watch(`form_fields.${index}.type`) ===
                              "select" && (
                              <FormField
                                control={form.control}
                                name={`form_fields.${index}.options`}
                                render={({ field: fieldCtrl }) => (
                                  <FormItem>
                                    <FormLabel>Options</FormLabel>
                                    <FormControl>
                                      <Textarea
                                        placeholder={
                                          "Option 1\nOption 2\nOption 3"
                                        }
                                        rows={3}
                                        value={
                                          optionsTextByFieldId[
                                            formFields[index].id
                                          ] ??
                                          (fieldCtrl.value?.join("\n") || "")
                                        }
                                        onChange={(e) => {
                                          const raw = e.target.value;
                                          setOptionsTextByFieldId((s) => ({
                                            ...s,
                                            [formFields[index].id]: raw,
                                          }));
                                        }}
                                        onBlur={(e) => {
                                          const normalized = e.target.value
                                            .split("\n")
                                            .map((opt) => opt.trim())
                                            .filter((opt) => opt.length > 0);
                                          fieldCtrl.onChange(normalized);
                                          setOptionsTextByFieldId((s) => ({
                                            ...s,
                                            [formFields[index].id]:
                                              normalized.join("\n"),
                                          }));
                                        }}
                                      />
                                    </FormControl>
                                    <FormDescription>
                                      Enter each option on a new line
                                    </FormDescription>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </Card>
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                <Button type="button" onClick={addFormField} variant="outline">
                  <Plus className="h-4 w-4 mr-2" /> Add Field
                </Button>
              </div>
            </TabsContent>
          )}

          {!isChatAssistant && (
            <TabsContent value="schema" className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-medium">Output formatting</h3>
                  <p className="text-sm text-muted-foreground">
                    Split your result into multiple sub-blocks (like title,
                    summary, content). You can nest sub-blocks up to 3 levels.
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={() => addZodProperty("")}
                  variant="outline"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add sub-block
                </Button>
              </div>

              <FormField
                control={form.control}
                name="generation_mode"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Structured Output
                      </FormLabel>
                      <FormDescription>
                        Enable to stream structured JSON (schema). Disable to
                        stream plain text.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={(field.value || "object") === "object"}
                          onChange={(e) =>
                            field.onChange(e.target.checked ? "object" : "text")
                          }
                        />
                        <div
                          className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${
                            (field.value || "object") === "object"
                              ? "bg-blue-600"
                              : "bg-gray-200"
                          }`}
                        >
                          <div
                            className={`absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform duration-200 ease-in-out ${
                              (field.value || "object") === "object"
                                ? "translate-x-5"
                                : "translate-x-0"
                            }`}
                          />
                        </div>
                      </label>
                    </FormControl>
                  </FormItem>
                )}
              />
              <PropertyRenderer
                key={`schema-${schemaVersion}-${Object.keys(zodSchema?.properties || {}).join("|")}-${(zodSchema?.required || []).join(",")}`}
                properties={zodSchema?.properties || {}}
                requiredNames={zodSchema?.required || []}
                parentPath=""
                level={0}
              />
            </TabsContent>
          )}
        </Tabs>

        <div className="flex justify-end gap-3 pt-6 border-t">
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : initialData ? "Update" : "Create"}{" "}
            Assistant
          </Button>
        </div>
      </form>
    </Form>
  );
};

// Lightweight industry select component using Supabase
const IndustrySelect: React.FC<{
  value: string;
  onChange: (v: string) => void;
}> = ({ value, onChange }) => {
  const [options, setOptions] = React.useState<
    { id: string; name: string; emoji?: string | null }[]
  >([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    supabaseClient
      .from("industries")
      .select("id, name, emoji")
      .order("position", { ascending: true })
      .order("name", { ascending: true })
      .then(({ data }) => {
        if (!mounted) return;
        setOptions(data || []);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const normalizedValue = value && value.length > 0 ? value : "__none__";

  return (
    <Select
      value={normalizedValue}
      onValueChange={(v) => onChange(v === "__none__" ? "" : v)}
    >
      <SelectTrigger>
        <SelectValue
          placeholder={loading ? "Loading..." : "Select industry (optional)"}
        />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">None</SelectItem>
        {options.map((opt) => (
          <SelectItem key={opt.id} value={opt.id}>
            {opt.emoji ? `${opt.emoji} ` : ""}
            {opt.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
